import { useMemo, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Header from './Header';
import Sidebar from '../Sidebar/Sidebar';
import SectionContent from '../Content/SectionContent';
import EmptyState from '../Content/EmptyState';
import { usePortalStore } from '../../stores/portalStore';
import { useNavTree } from '../../hooks/useNavTree';
import { useCardMetadata } from '../../hooks/useCardMetadata';
import { useDriveMetadata } from '../../hooks/useDriveMetadata';
import { useAuth } from '../../hooks/useAuth';
import { createCard, updateCard, deleteCard, getCardsForPages } from '../../services/cardMetadataService';
import { deleteUpload, isUploadPath, sharesUploadFolder } from '../../services/uploadApi';
import { parseDriveUrl } from '../../utils/driveUrl';
import type { BlockType } from '../../types/nav';
import type { AddCardExtra } from '../../types/addCard';

export default function Layout() {
  const { selectedSectionId, setSelectedSection } = usePortalStore();
  const {
    sections, pages, blocks, loading: navLoading,
    addPage, addBlock, updateNode, deleteNode, movePageToBlock, reorderNodes,
  } = useNavTree();
  const { user } = useAuth();
  const { fetchAndCacheMetadata } = useDriveMetadata();

  useEffect(() => {
    if (!selectedSectionId && sections.length > 0) {
      setSelectedSection(sections[0].id);
    }
  }, [selectedSectionId, sections, setSelectedSection]);

  const cardIds = useMemo(() => {
    if (!selectedSectionId) return [];
    return pages
      .filter((p) => p.parentId === selectedSectionId && p.cardId)
      .map((p) => p.cardId!);
  }, [selectedSectionId, pages]);

  const { cards, loading: cardsLoading } = useCardMetadata(cardIds);

  const sectionBlocks = useMemo(() => {
    if (!selectedSectionId) return [];
    return blocks
      .filter((b) => b.parentId === selectedSectionId)
      .sort((a, b) => a.order - b.order);
  }, [selectedSectionId, blocks]);

  const sectionPages = useMemo(() => {
    if (!selectedSectionId) return [];
    return pages.filter((p) => p.parentId === selectedSectionId);
  }, [selectedSectionId, pages]);

  const handleAddCard = useCallback(async (title: string, url: string, summary: string, blockId?: string, extra?: AddCardExtra): Promise<string> => {
    if (!selectedSectionId || !user) return '';

    const parsed = url ? parseDriveUrl(url) : null;
    const isWebUrl = !parsed && url && (url.startsWith('http://') || url.startsWith('https://'));
    // An uploaded file forces its page type; otherwise infer from the URL.
    const pageType = extra?.pageType
      ? extra.pageType
      : parsed
      ? parsed.mimeType.includes('spreadsheet') ? 'sheet' as const
        : parsed.mimeType.includes('presentation') ? 'slides' as const
        : 'doc' as const
      : isWebUrl ? 'website' as const
      : url ? 'external' as const : 'guide' as const;

    const cardId = await createCard({
      title,
      summary: summary || title,
      type: pageType,
      url: url || '',
      ...(extra?.thumbnailUrl ? { thumbnailUrl: extra.thumbnailUrl } : {}),
      ...(parsed?.docId ? { driveDocId: parsed.docId } : {}),
      createdBy: user.uid,
      updatedBy: user.uid,
    });

    // Write the page directly into its target block (if any) -- avoids a racey
    // post-hoc move against stale snapshot state.
    await addPage(selectedSectionId, title, url || '', pageType, cardId, blockId);

    if (url && parsed) {
      fetchAndCacheMetadata(url, cardId).catch(() => {});
    }

    return cardId;
  }, [selectedSectionId, user, addPage, fetchAndCacheMetadata]);

  const handleUpdateCard = useCallback(async (
    cardId: string,
    updates: { title: string; summary: string; url: string },
  ) => {
    if (!user) return;
    await updateCard(cardId, { ...updates, updatedBy: user.uid });
    const pageNode = pages.find((p) => p.cardId === cardId);
    if (pageNode) {
      await updateNode(pageNode.id, { title: updates.title, url: updates.url });
    }
  }, [user, pages, updateNode]);

  const handleSetThumbnail = useCallback(async (cardId: string, thumbnailUrl: string) => {
    if (!user) return;
    const prev = cards.find((c) => c.id === cardId);
    await updateCard(cardId, { thumbnailUrl, updatedBy: user.uid });
    // Clean up a previously-uploaded thumbnail being replaced -- but only if its
    // folder isn't shared with the card's own uploaded file.
    const prevThumb = prev?.thumbnailUrl;
    if (prevThumb && prevThumb !== thumbnailUrl && !sharesUploadFolder(prevThumb, prev?.url)) {
      deleteUpload(prevThumb);
    }
  }, [user, cards]);

  const handleDeleteCard = useCallback(async (cardId: string) => {
    const card = cards.find((c) => c.id === cardId);
    const pageNode = pages.find((p) => p.cardId === cardId);
    if (pageNode) await deleteNode(pageNode.id);
    await deleteCard(cardId);
    // Best-effort GCS cleanup. The folder delete removes file + thumbnail together;
    // a separately-uploaded custom thumbnail (different folder) is cleaned too.
    if (card) {
      if (isUploadPath(card.url)) deleteUpload(card.url);
      if (card.thumbnailUrl && !sharesUploadFolder(card.thumbnailUrl, card.url)) {
        deleteUpload(card.thumbnailUrl);
      }
    }
  }, [cards, pages, deleteNode]);

  const handleRenameSection = useCallback(async (sectionId: string, title: string) => {
    await updateNode(sectionId, { title });
  }, [updateNode]);

  const handleDeleteSection = useCallback(async (sectionId: string) => {
    const childPages = pages.filter((p) => p.parentId === sectionId);
    // Fetch this section's card metadata up front so we can clean up any GCS uploads.
    const childCardIds = childPages.map((p) => p.cardId).filter(Boolean) as string[];
    const childCards = childCardIds.length ? await getCardsForPages(childCardIds) : [];
    for (const page of childPages) {
      if (page.cardId) await deleteCard(page.cardId);
      await deleteNode(page.id);
    }
    for (const card of childCards) {
      if (isUploadPath(card.url)) deleteUpload(card.url);
      if (card.thumbnailUrl && !sharesUploadFolder(card.thumbnailUrl, card.url)) {
        deleteUpload(card.thumbnailUrl);
      }
    }
    const childBlocks = blocks.filter((b) => b.parentId === sectionId);
    for (const block of childBlocks) {
      await deleteNode(block.id);
    }
    await deleteNode(sectionId);
  }, [pages, blocks, deleteNode]);
  // (uses module-level getCardsForPages / deleteUpload helpers; no extra reactive deps)

  const handleAddBlock = useCallback(async (blockType: BlockType, title: string) => {
    if (!selectedSectionId) return;
    await addBlock(selectedSectionId, blockType, title);
  }, [selectedSectionId, addBlock]);

  const handleUpdateBlock = useCallback(async (blockId: string, title: string) => {
    await updateNode(blockId, { title });
  }, [updateNode]);

  const handleDeleteBlock = useCallback(async (blockId: string) => {
    // Ungroup pages belonging to this block
    const groupedPages = pages.filter((p) => p.blockId === blockId);
    for (const page of groupedPages) {
      await movePageToBlock(page.id, null);
    }
    await deleteNode(blockId);
  }, [pages, movePageToBlock, deleteNode]);

  const handleMovePageToBlock = useCallback(async (pageId: string, blockId: string | null) => {
    await movePageToBlock(pageId, blockId);
  }, [movePageToBlock]);

  const handleReorderBlocks = useCallback(async (blockIds: string[]) => {
    await reorderNodes(blockIds);
  }, [reorderNodes]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Sidebar
          onRenameSection={handleRenameSection}
          onDeleteSection={handleDeleteSection}
        />
        <Box
          component="main"
          sx={{
            flex: 1,
            p: { xs: 3, sm: 4, md: 6 },
            maxWidth: 1060,
            mx: 'auto',
            width: '100%',
            overflowY: 'auto',
          }}
        >
          {selectedSectionId ? (
            <SectionContent
              sectionId={selectedSectionId}
              blocks={sectionBlocks}
              pages={sectionPages}
              cards={cards}
              loading={navLoading || cardsLoading}
              onAddCard={handleAddCard}
              onUpdateCard={handleUpdateCard}
              onSetThumbnail={handleSetThumbnail}
              onDeleteCard={handleDeleteCard}
              onAddBlock={handleAddBlock}
              onUpdateBlock={handleUpdateBlock}
              onDeleteBlock={handleDeleteBlock}
              onMovePageToBlock={handleMovePageToBlock}
              onReorderBlocks={handleReorderBlocks}
            />
          ) : (
            <EmptyState hasSection={false} />
          )}
        </Box>
      </Box>
    </Box>
  );
}
